import React from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  Zap,
  Settings,
  LogOut,
  Compass,
  ListTodo,
  GraduationCap,
  Calendar,
  MoreHorizontal,
  BookOpen
} from 'lucide-react'
import { useSidebarStore } from '@/shared/store/sidebarStore'
import { useAuth } from '@/platform/auth/useAuth'

import styles from './Sidebar.module.css'

interface NavEntry {
  id: string
  name: string
  route: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  isActive: (pathname: string) => boolean
}

const DESKTOP_NAV_ITEMS: NavEntry[] = [
  {
    id: 'today',
    name: 'Today',
    route: '/dashboard',
    icon: Compass,
    isActive: (p) => p === '/dashboard' || p === '/today' || p === '/'
  },
  {
    id: 'plan',
    name: 'Plan',
    route: '/plan',
    icon: ListTodo,
    isActive: (p) => p.startsWith('/plan') || p.startsWith('/planner') || p.startsWith('/goals')
  },
  {
    id: 'learn',
    name: 'Learn',
    route: '/learning',
    icon: GraduationCap,
    isActive: (p) =>
      p.startsWith('/learning') ||
      p.startsWith('/french') ||
      p.startsWith('/language-goal') ||
      p.startsWith('/celpip')
  },
  {
    id: 'calendar',
    name: 'Calendar',
    route: '/calendar',
    icon: Calendar,
    isActive: (p) => p.startsWith('/calendar')
  },
  {
    id: 'more',
    name: 'More & Tools',
    route: '/more',
    icon: MoreHorizontal,
    isActive: (p) =>
      p.startsWith('/more') ||
      p.startsWith('/settings') ||
      p.startsWith('/profile')
  }
]

export function Sidebar() {
  const { isCollapsed, toggleCollapsed } = useSidebarStore()
  const { logout, user, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <aside className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''}`}>
      {/* Brand */}
      <div className={styles.brand}>
        <div className={styles.logo}>
          <Zap size={20} strokeWidth={2.5} />
        </div>
        {!isCollapsed && (
          <span className={styles.brandName}>NTTD</span>
        )}
        <button
          className={styles.collapseBtn}
          onClick={toggleCollapsed}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className={styles.nav}>
        <span className={styles.navLabel}>{!isCollapsed && 'Navigation'}</span>
        {DESKTOP_NAV_ITEMS.map((item) => {
          const Icon = item.icon
          const active = item.isActive(location.pathname)

          return (
            <NavLink
              key={item.id}
              to={item.route}
              className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
              title={isCollapsed ? item.name : undefined}
            >
              <span className={styles.navIcon}>
                <Icon size={18} />
              </span>
              {!isCollapsed && <span className={styles.navLabel}>{item.name}</span>}
            </NavLink>
          )
        })}

        {/* Secondary quick link for Bhagavad Gita */}
        {!isCollapsed && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <span className={styles.navLabel}>Featured</span>
            <NavLink
              to="/gita"
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
            >
              <span className={styles.navIcon}>
                <BookOpen size={18} />
              </span>
              <span className={styles.navLabel}>Bhagavad Gita</span>
            </NavLink>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className={styles.footer}>
        <NavLink
          to="/more"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
          }
          title={isCollapsed ? 'Settings & Preferences' : undefined}
        >
          <span className={styles.navIcon}><Settings size={18} /></span>
          {!isCollapsed && <span>Settings</span>}
        </NavLink>

        {isLoading ? (
          <div
            className={styles.navItem}
            style={{ opacity: 0.6, cursor: 'default', pointerEvents: 'none' }}
            aria-hidden="true"
          >
            <span className={styles.navIcon}>
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid currentColor',
                  borderTopColor: 'transparent',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            </span>
            {!isCollapsed && <span style={{ fontSize: '0.8125rem' }}>Checking session…</span>}
          </div>
        ) : isAuthenticated ? (
          <button
            className={`${styles.navItem} ${styles.logoutBtn}`}
            onClick={handleLogout}
            title={isCollapsed ? 'Logout' : undefined}
          >
            <span className={styles.navIcon}><LogOut size={18} /></span>
            {!isCollapsed && <span>Logout</span>}
          </button>
        ) : (
          <NavLink
            to="/login"
            className={styles.navItem}
            title={isCollapsed ? 'Login' : undefined}
          >
            <span className={styles.navIcon}><LogOut size={18} /></span>
            {!isCollapsed && <span>Login</span>}
          </NavLink>
        )}

        {!isCollapsed && !isLoading && user && (
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className={styles.userDetails}>
              <span className={styles.userName}>{user.name}</span>
              <span className={styles.userEmail}>{user.email}</span>
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}
